import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {FaCheck, FaCircle, FaEraser, FaFileImport, FaHistory, FaPlus, FaSearch} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import citizenService from "../../../../../services/web/citizen-service.js";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import LifeRisk from "../utils/life-risk.js";
import {MaskDefault} from "../../../../../utils/mask.js";
import projectUtils from "../../../../../utils/project-utils.js";
import {FaFilePen, FaTriangleExclamation} from "react-icons/fa6";
import CitizenImport from "./components/citizen-import.jsx";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import protectionProgramService from "../../../../../services/web/protection-program-service.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import useHasPermission from "../../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";

const initialFilters = {
    lifeRisk: "",
    name: "",
    protectionProgram: [],
    status: "",
    cpf: "",
    phone: "",
    citizenAddress: "",
    placeAddress: "",
    processNumber: "",
    aggressorName: "",
    identificationNumber: "",
    dateExpiration: "",
    with_form: "all",
    citizen_type: "all",
}

const CitizensList = () => {
    const navigate = navigateHook()

    const [filter, setFilter] = useState({filter:[],sort:[{field:"name",dir:"asc"}]});
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [importModal, setImportModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const hasPermission = useHasPermission();

    const {data, refetch} = useQuery({
        queryFn: () => citizenService.list(filter).then(({data}) => data),
        queryKey: ["list", {filter}]
    });

    const {data: programs} = useQuery({
        queryFn: () => protectionProgramService.options(),
        queryKey: ["program"]
    });

    const [columnDefs,] = useState([
        { field: "id", headerName: "ID", width: 60},
        { field: "lifeRisk", headerName: "Risco", width: 70, sortable: false,
            cellRenderer: ({data}) => {
                if (data.lifeRisk) {
                    const color = LifeRisk.getLifeRiskColor(data.lifeRisk);
                    return (
                        <div className={"flex items-center justify-center h-full w-full"}>
                            <FaCircle color={color} size={"20px"}/>
                        </div>
                    )
                }

                return '';
            }
        },
        { field: "identificationNumber", headerName: "Número de  identificação", flex: 1, comparator: aggridUtils.nullLastComparator},
        { field: "name", headerName: "Nome/Razão Social", flex: 1, comparator: aggridUtils.nullLastComparator},
        { field: "protectionProgram", headerName: "Programa de proteção", flex: 1, comparator: aggridUtils.nullLastComparator},
        { field: "status", headerName: "Status", width: 100, valueFormatter: ({value}) => value === "ACTIVATED" ? "Ativo" : "Inativo"},
        { field: "cpf", headerName: "CPF/CPNJ", flex: 1, valueFormatter: ({data: {cpf, cnpj}}) => cpf ? projectUtils.formatCpfToView(cpf) : projectUtils.formatCnpjToView(cnpj)},
        { field: "phone", headerName: "Telefone", flex: 1, valueFormatter: ({value}) => projectUtils.formatPhone(value), comparator: aggridUtils.nullLastComparator},
        { field: "registrationDt", headerName: "Cadastro", flex: 1, comparator: aggridUtils.agGridCompareDateTime },
        { field: "lastLoginDt", headerName: "Último acesso", flex: 1, comparator: aggridUtils.agGridCompareDateTime},
        { field: "autoCheckupDt", headerName: "Teste App", width: 110,
            cellRenderer: ({data}) => {
                let message;
                if (data.noApp) {
                    return "Não utiliza"
                }

                const title = data?.autoCheckupDt ?? ""

                if (data.autoCheckupOk) {
                    message = <FaCheck color={"#0cda0c"} title={title}/>;
                } else {
                    message = <FaTriangleExclamation color={"#ed5565"} title={title}/>;
                }

                return (
                    <div className={"flex items-center justify-center h-full w-full"}>
                        {message}
                    </div>
                )
            }
        },
        { field: "lastForm", headerName: "Último Formulário", flex: 1, comparator: aggridUtils.nullLastComparator},
        {
            headerName: "", field: "open-history", width: 70, sortable: false,
            cellRenderer: ({data}) => {
                return (
                    <div className={"flex items-center justify-center h-full w-full"}>
                        <Button className={"w-7.5! h-6.25! p-0!"}>
                            <FaHistory />
                        </Button>
                    </div>
                )
            }
        }
    ])

    const gridRef = useRef(undefined);

    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    const onCellClicked = ({data, event, colDef}) => {
        let url = `/citizens/${data.id}/edit`;
        let devUrl = `/sos/citizens/form/${data.id}`
        const isBlank = event.ctrlKey || colDef.field === "open-history";

        if (colDef.field === "open-history") {
            localStorage.setItem("open-history", "true");

            url = "/citizenHistory/" + data.id + "/edit";
            devUrl = "/sos/citizen/history/" + data.id
        }

        navigate(devUrl, isBlank);
    }

    const handleInput = ({target: {id, value}}) => setFilterValues(prev => ({...prev, [id]: id === "cnpj" ? value?.toUpperCase() : value}));

    const handleSelect = (id, selected) => setFilterValues(prev => ({...prev, [id]: selected?.value}));

    const apply = useCallback(() => {
        const filter = Object.keys(filterValues)
            .filter(field => field !== "protectionProgram" && field !== "cpf")
            .map(field => ({field, type: "like", value: filterValues[field]}));

        filter.push({field: "protectionProgram", type: "like", value: filterValues.protectionProgram?.map(p => p.value).join(",") ?? ""})
        filter.push({field: "cpf", type: "like", value: filterValues.cpf?.replace(/[^a-zA-Z0-9]/g, '') ?? ""})

        setFilter(prev =>
            ({
                ...prev,
                filter
            })
        )
    }, [filterValues])

    const clear = useCallback(() => {
        setFilterValues(initialFilters)
        setFilter({
            filter: Object.keys(initialFilters).map(field => ({field, type: "like", value: initialFilters[field]})),
            page:1,
            size:500,
            sort:[{field:"name",dir:"asc"}]
        });
    }, [])

    useGlobalKey("Enter", apply)
    useGlobalKey("Escape", clear)

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>
                        Cadastros
                    </Template.Title>

                    {
                        hasPermission(PermissionEnum.SOS_MANAGE_CITIZEN) &&
                        <div className={"flex flex-row gap-2"}>
                            <Button target={"success"} onClick={() => setImportModal(true)}>
                                <FaFileImport /> Importar
                            </Button>
                            <Button target={"primary"} onClick={() => navigate("/sos/citizens/form")}>
                                <FaPlus/> Adicionar cadastro
                            </Button>
                        </div>
                    }
                </Template.Header>

                <Template.Body className={"space-y-2"}>
                    <Card>
                        <Card.Body className={"grid grid-cols-12 gap-4"}>
                            <SelectComponent
                                classNameWrapper={"col-span-2"}
                                value={filterValues.lifeRisk}
                                options={LifeRisk.getLifeRisk()}
                                label={"Grau de risco"}
                                onChange={(value) => handleSelect("lifeRisk", value)}
                            />

                            <InputForm
                                classNameWrapper={"col-span-5"}
                                id={"name"}
                                label={"Nome/Razão Social"}
                                value={filterValues.name}
                                onChange={handleInput}
                            />

                            <InputForm
                                classNameWrapper={"col-span-2"}
                                id={"cpf"}
                                label={"CPF/CNPJ"}
                                value={filterValues.cpf}
                                onChange={handleInput}
                                mask={MaskDefault.cpfAndCnpj}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-3"}
                                value={filterValues.protectionProgram}
                                options={programs ?? []}
                                label={"Programa"}
                                isMulti
                                onChange={(value) => setFilterValues(prev => ({...prev, protectionProgram: value}))}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-2"}
                                value={filterValues.status}
                                options={[{value:"ACTIVATED", label: "Ativo"}, {value:"INACTIVATED", label: "Inativo"}]}
                                label={"Status"}
                                onChange={(value) => handleSelect("status", value)}
                            />

                            <InputForm
                                classNameWrapper={"col-span-2"}
                                id={"phone"}
                                label={"Telefone"}
                                value={filterValues.phone}
                                onChange={handleInput}
                                mask={MaskDefault.phone}
                            />

                            <InputForm
                                classNameWrapper={"col-span-6"}
                                id={"citizenAddress"}
                                label={"Endereço"}
                                value={filterValues.citizenAddress}
                                onChange={handleInput}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-2"}
                                value={filterValues.citizen_type}
                                options={[{value:"all", label: "Todos"}, {value:"CPF", label: "CPF"}, {value:"CNPJ", label: "CNPJ"}]}
                                label={"Tipo de cadastro"}
                                onChange={(value) => handleSelect("citizen_type", value)}
                                isClearable={false}
                            />

                            <InputForm
                                classNameWrapper={"col-span-2"}
                                id={"processNumber"}
                                label={"Nº do processo"}
                                value={filterValues.processNumber}
                                onChange={handleInput}
                            />

                            <InputForm
                                classNameWrapper={"col-span-2"}
                                id={"identificationNumber"}
                                label={"Nº de identificação"}
                                value={filterValues.identificationNumber}
                                onChange={handleInput}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-2"}
                                value={filterValues.dateExpiration}
                                options={[
                                    {value:"30", label: "Em 30 dias"},
                                    {value:"60", label: "Em 60 dias"},
                                    {value:"90", label: "Em 90 dias"},
                                    {value:"120", label: "Em 120 dias"},
                                    {value:"expired", label: "Expirado"}
                                ]}
                                label={"Expiração da medida protetiva"}
                                onChange={(value) => handleSelect("dateExpiration", value)}
                                isClearable={false}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-2"}
                                value={filterValues.with_form}
                                options={[{value:"all", label: "Todos"}, {value:"true", label: "Sim"}, {value:"false", label: "Não"}]}
                                label={"Com formulário"}
                                onChange={(value) => handleSelect("with_form", value)}
                                isClearable={false}
                            />

                            <div className={"col-span-4 flex justify-between gap-4"}>
                                <InputForm
                                    classNameWrapper={"w-full"}
                                    id={"aggressorName"}
                                    label={"Nome do agressor"}
                                    value={filterValues.aggressorName}
                                    onChange={handleInput}
                                />

                                <div className={"flex gap-2"}>
                                    <Button target={"default"} onClick={clear}>
                                        <FaEraser />
                                    </Button>
                                    <Button onClick={apply}>
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Body>
                            <div className={"h-[calc(100vh-249px)] aggrid-row-pointer"}>
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={data?.data ?? []}
                                    columnDefs={columnDefs}
                                    pagination
                                    paginationPageSize={100}
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    onCellClicked={onCellClicked}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={theme}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <CitizenImport
                open={importModal}
                setOpen={setImportModal}
                refetch={refetch}
                setIsLoading={setIsLoading}
                programs={programs}
            />
        </>
    );
};

export default CitizensList;